import {
  Injector,
  LOCALE_ID,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {
  TUI_ICONS_PATH,
  TuiButtonModule,
  TuiRootModule,
  TuiScrollbarModule,
} from '@taiga-ui/core';
import {AppComponent} from './app.component';
import {StoreModule} from './store/store.module';
import {NgxsReduxDevtoolsPluginModule} from '@ngxs/devtools-plugin';
import {NgxsActionsExecutingModule} from '@ngxs-labs/actions-executing';
import {AppRoutingModule} from './app-routing.module';
import {FooterModule} from '@ui/footer/footer.module';
import {HeaderModule} from '@ui/header/header.module';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  TISA_WICKET_ENTITLEMENTS_API_URL,
  TISA_WICKET_TOKEN,
  TISA_WICKET_URL,
  TISA_WICKET_V2_API_URL,
} from '@tisa-group/core/api/wicket/tisa-wicket.providers';
import {environment} from '@environments/environment';
import {LOCAL_STORAGE, WINDOW} from '@ng-web-apis/common';
import {WICKET_TOKEN_KEY} from '@core/constants/localstorage-keys';
import {AuthInterceptor} from '@core/http/auth.interceptor';
import {iconsPath} from './icons-path.factory';
import {TISA_CLOUDFRONT_ID, TisaUmpireModule} from '@tisa-group/core';
import {
  TISA_UMPIRE_TOKEN,
  TISA_UMPIRE_URL,
} from '@tisa-group/core/api/umpire/tisa-umpire.providers';
import {LoaderModule} from '@ui/loader/loader.module';
import {UmpireHttpErrorInterceptor} from '@core/http/page-not-found.interceptor';
import {ViewComingSoonModule} from '@views/view-coming-soon/view-coming-soon.module';
import {CookiesModule} from '@ui/cookies/cookies.module';
import {MultiTranslateHttpLoader} from 'ngx-translate-multi-http-loader';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {registerLocaleData} from '@angular/common';
import localePl from '@angular/common/locales/pl';
import localeEn from '@angular/common/locales/en';
import localeLt from '@angular/common/locales/lt';
import localePt from '@angular/common/locales/pt';
import localeFr from '@angular/common/locales/fr';

import {LanguageService} from '@core/services/language.service';
import {ASSET_MAP, ASSET_MAPS} from '@core/constants/client-asset-maps';
import {ConsentBannerModule} from '@ui/consent-banner/consent-banner.module';
import {AppInitializer} from './app.initializer';
import {LanguageInterceptor} from '@core/http/language.interceptor';

import {Store} from '@ngxs/store';
import {Router, TitleStrategy} from '@angular/router';
import {routerConfigInitializerFactory} from './router-config.initializer';
import {PreviewDataInitializer} from '@core/services/preview-data-initializer';
import {globalBrandingInitializerFactory} from './global-branding.initializer';
import {currentClientInitializerFactory} from './current-client.initializer';
import {CurrentClientState} from '@store/current-client/current-client.state';
import {TrackingService} from '@core/services/tracking.service';
import {TemplatePageTitleStrategy} from '@core/utils/title-strategy';
import {detectLanguageInitializerFactory} from './language.initializer';
import {RouterInitStatusService} from '@core/services/router-init-status.service';
import {BrndtsService} from '@core/services/brndts.service';
import {CMPService} from '@core/services/cmp.service';
import {TuiLetModule} from '@taiga-ui/cdk';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new MultiTranslateHttpLoader(httpClient, [
    {prefix: './assets/i18n/default/', suffix: '.json'},
    {prefix: `./assets/i18n/${environment.name}/`, suffix: '.json'},
    {prefix: './assets/i18n/client-ui/', suffix: '.json'},
  ]);
}
registerLocaleData(localeEn);
registerLocaleData(localePl);
registerLocaleData(localeFr);
registerLocaleData(localeLt);
registerLocaleData(localePt);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    TuiRootModule,
    StoreModule,
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsActionsExecutingModule.forRoot(),
    AppRoutingModule,
    FooterModule,
    HeaderModule,
    TuiButtonModule,
    HeaderModule,
    LoaderModule,
    ViewComingSoonModule,
    CookiesModule,
    TuiScrollbarModule,
    TuiLetModule,
    TisaUmpireModule.forRoot({
      api: environment.clientConfig.apiUrls.umpire,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ConsentBannerModule,
  ],
  providers: [
    AppInitializer,
    provideAppInitializer(() => {
      const initializerFn = routerConfigInitializerFactory(
        inject(Store),
        inject(Router),
        inject(PreviewDataInitializer),
        inject(RouterInitStatusService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = globalBrandingInitializerFactory(
        inject(Store),
        inject(Injector),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = currentClientInitializerFactory(
        inject(Store),
        inject(Injector),
        inject(TrackingService),
        inject(BrndtsService),
        inject(CMPService),
      );
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = detectLanguageInitializerFactory(inject(Injector));
      return initializerFn();
    }),
    {
      provide: TUI_ICONS_PATH,
      useValue: iconsPath,
    },
    {
      provide: TISA_WICKET_TOKEN,
      useFactory: (localStorage: Storage) => {
        return () => localStorage.getItem(WICKET_TOKEN_KEY);
      },
      deps: [LOCAL_STORAGE],
    },
    {
      provide: TISA_UMPIRE_URL,
      useValue: environment.clientConfig.apiUrls.umpire,
    },
    {
      provide: TISA_UMPIRE_TOKEN,
      // For multitenant smart stride setup umpire can handle hostname as apiKey
      useFactory: (window: Window) => () =>
        environment.clientConfig.umpire.apiKey || window.location.hostname,
      deps: [WINDOW],
    },
    {
      provide: TISA_CLOUDFRONT_ID,
      useFactory: (store: Store) => {
        return store.selectSnapshot(CurrentClientState.getData)?.config
          .external_dependencies.cloudfront_id;
      },
      deps: [Store],
    },
    {
      provide: TISA_WICKET_V2_API_URL,
      useFactory: (store: Store) => () => {
        const baseApi = store.selectSnapshot(CurrentClientState.getData)?.config
          .external_dependencies.wicket?.url;
        if (!baseApi) {
          return undefined;
        }
        return `${baseApi}/api/v2`;
      },
      deps: [Store],
    },
    {
      provide: TISA_WICKET_URL,
      useFactory: (store: Store) => () => {
        return store.selectSnapshot(CurrentClientState.getData)?.config
          .external_dependencies.wicket?.frontend_url;
      },
      deps: [Store],
    },
    {
      provide: TISA_WICKET_ENTITLEMENTS_API_URL,
      useFactory: (store: Store) => () => {
        const baseApi = store.selectSnapshot(CurrentClientState.getData)?.config
          .external_dependencies.wicket?.url;
        if (!baseApi) {
          return undefined;
        }
        return `${baseApi}/entitlements/api`;
      },
      deps: [Store],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UmpireHttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: (languageService: LanguageService) =>
        languageService.currentLanguage$.getValue() || 'en',
      deps: [LanguageService],
    },
    {
      provide: ASSET_MAP,
      useValue: ASSET_MAPS[environment.name as keyof typeof ASSET_MAPS],
    },
    {provide: TitleStrategy, useClass: TemplatePageTitleStrategy},
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
